<script setup>
import { ref, computed, onMounted } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import AiAgentModal from './components/AiAgentModal.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import StudioBadge from './studio/components/StudioBadge.vue';

const store = useStore();
const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const agentBots = useMapGetter('agentBots/getBots');
const uiFlags = useMapGetter('agentBots/getUIFlags');
const inboxes = useMapGetter('inboxes/getInboxes');

const aiAgentModalRef = ref(null);
const aiAgentDeleteDialogRef = ref(null);
const loading = ref({});
const selectedBot = ref({});

const aiAgents = computed(() =>
  agentBots.value.filter(bot => bot.bot_type === 'aegis')
);

const selectedBotName = computed(() => selectedBot.value?.name || '');

const inboxCount = bot =>
  inboxes.value.filter(
    inbox =>
      store.getters['agentBots/getActiveAgentBot'](inbox.id)?.id === bot.id
  ).length;

const modelLabel = () => t('AI_AGENT.ENGINE_NAME');

const CAPABILITIES = [
  { key: 'PERSONA', icon: 'i-lucide-user-round' },
  { key: 'KNOWLEDGE', icon: 'i-lucide-book-open' },
  { key: 'HANDOFF', icon: 'i-lucide-life-buoy' },
];

const openCreateModal = () => {
  aiAgentModalRef.value.dialogRef.open();
};

const openDeletePopup = bot => {
  selectedBot.value = bot;
  aiAgentDeleteDialogRef.value.open();
};

const openStudio = bot => {
  router.push({
    name: 'ai_agents_studio',
    params: { botId: bot.id },
  });
};

const handleCreated = bot => {
  router.push(
    `/app/accounts/${route.params.accountId}/settings/ai-agents/${bot.id}/studio/persona`
  );
};

const deleteAiAgent = async id => {
  try {
    await store.dispatch('agentBots/delete', id);
    useAlert(t('AI_AGENT.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('AI_AGENT.DELETE.API.ERROR_MESSAGE'));
  } finally {
    loading.value[id] = false;
    selectedBot.value = {};
  }
};

const confirmDeletion = () => {
  loading.value[selectedBot.value.id] = true;
  deleteAiAgent(selectedBot.value.id);
  aiAgentDeleteDialogRef.value.close();
};

onMounted(async () => {
  await store.dispatch('agentBots/get');
  await store.dispatch('inboxes/get');
  inboxes.value.forEach(inbox => {
    store.dispatch('agentBots/fetchAgentBotInbox', inbox.id);
  });
  if (route.name === 'ai_agents_new') {
    openCreateModal();
  }
});
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="t('AI_AGENT.LIST.LOADING')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="t('AI_AGENT.HEADER')"
        :description="t('AI_AGENT.DESCRIPTION')"
        feature-name="ai_bot"
      >
        <template v-if="aiAgents.length" #count>
          <span class="text-body-main text-n-slate-11">
            {{ $t('AI_AGENT.COUNT', { n: aiAgents.length }) }}
          </span>
        </template>
        <template #actions>
          <Button
            :label="$t('AI_AGENT.CREATE.BUTTON')"
            size="sm"
            icon="i-lucide-plus"
            @click="openCreateModal"
          />
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <section
        v-if="!aiAgents.length"
        class="relative flex flex-col items-center gap-6 px-6 py-12 overflow-hidden text-center rounded-2xl bg-n-solid-2 outline outline-1 -outline-offset-1 outline-n-container"
        data-testid="ai-agents-empty"
      >
        <div
          class="absolute rounded-full pointer-events-none -top-32 left-1/2 -translate-x-1/2 size-80 bg-n-brand/10 blur-3xl"
          aria-hidden="true"
        />
        <span
          class="relative flex items-center justify-center rounded-2xl size-14 bg-gradient-to-br from-n-brand to-n-iris-9 text-white shadow-lg"
        >
          <span class="i-lucide-bot size-7" />
        </span>
        <div class="relative flex flex-col max-w-lg gap-2">
          <h2 class="text-heading-1 text-n-slate-12">
            {{ $t('AI_AGENT.LIST.EMPTY.TITLE') }}
          </h2>
          <p class="text-body-para text-n-slate-11">
            {{ $t('AI_AGENT.LIST.EMPTY.DESCRIPTION') }}
          </p>
        </div>
        <ul class="relative grid w-full max-w-2xl grid-cols-1 gap-3 sm:grid-cols-3">
          <li
            v-for="capability in CAPABILITIES"
            :key="capability.key"
            class="flex flex-col items-start gap-2 p-3 text-left rounded-xl bg-n-alpha-1 outline outline-1 -outline-offset-1 outline-n-weak"
          >
            <span :class="capability.icon" class="size-4 text-n-blue-11" />
            <span class="text-heading-3 text-n-slate-12">
              {{ $t(`AI_AGENT.LIST.EMPTY.CAPABILITIES.${capability.key}.TITLE`) }}
            </span>
            <span class="text-label-small text-n-slate-11">
              {{ $t(`AI_AGENT.LIST.EMPTY.CAPABILITIES.${capability.key}.TEXT`) }}
            </span>
          </li>
        </ul>
        <Button
          class="relative"
          icon="i-lucide-sparkles"
          :label="$t('AI_AGENT.CREATE.BUTTON')"
          data-testid="ai-agents-empty-create"
          @click="openCreateModal"
        />
      </section>

      <ul v-else class="grid grid-cols-1 gap-4 md:grid-cols-2">
        <li
          v-for="bot in aiAgents"
          :key="bot.id"
          class="relative flex flex-col gap-4 p-5 transition-shadow group rounded-xl bg-n-solid-2 outline outline-1 -outline-offset-1 outline-n-container hover:shadow-md focus-within:shadow-md motion-reduce:transition-none"
          :data-testid="`ai-agent-card-${bot.id}`"
        >
          <div class="flex items-start gap-3">
            <div
              class="p-0.5 rounded-full shrink-0"
              :class="
                inboxCount(bot)
                  ? 'bg-gradient-to-br from-n-brand via-n-iris-9 to-n-teal-9'
                  : 'bg-n-alpha-2'
              "
            >
              <div class="p-0.5 rounded-full bg-n-solid-2">
                <Avatar
                  :name="bot.name || ''"
                  :src="bot.thumbnail"
                  :size="44"
                  rounded-full
                />
              </div>
            </div>
            <div class="flex flex-col flex-1 min-w-0 gap-1">
              <div class="flex items-center min-w-0 gap-2">
                <button
                  type="button"
                  class="truncate text-left text-heading-2 text-n-slate-12 after:absolute after:inset-0 after:rounded-xl focus-visible:outline-none focus-visible:after:ring-2 focus-visible:after:ring-n-brand"
                  @click="openStudio(bot)"
                >
                  {{ bot.name }}
                </button>
                <StudioBadge dot :tone="inboxCount(bot) ? 'teal' : 'amber'">
                  {{
                    inboxCount(bot)
                      ? $t('AI_AGENT.STUDIO.STATUS.ACTIVE')
                      : $t('AI_AGENT.STUDIO.STATUS.INACTIVE')
                  }}
                </StudioBadge>
              </div>
              <p class="line-clamp-2 text-body-main text-n-slate-11">
                {{ bot.description || $t('AI_AGENT.STUDIO.HERO.NO_DESCRIPTION') }}
              </p>
            </div>
          </div>
          <div class="flex flex-wrap items-center gap-2">
            <StudioBadge>
              <span class="i-lucide-sparkles size-3" />
              {{ modelLabel(bot) || $t('AI_AGENT.STUDIO.HERO.NOT_SET') }}
            </StudioBadge>
            <StudioBadge>
              <span class="i-lucide-inbox size-3" />
              {{ $t('AI_AGENT.LIST.INBOX_COUNT', { n: inboxCount(bot) }) }}
            </StudioBadge>
            <div class="relative z-10 flex items-center gap-1 ml-auto">
              <Button
                v-tooltip.top="t('AI_AGENT.LIST.OPEN_STUDIO')"
                icon="i-lucide-settings-2"
                slate
                ghost
                xs
                @click="openStudio(bot)"
              />
              <Button
                v-tooltip.top="t('AI_AGENT.DELETE.BUTTON_TEXT')"
                icon="i-woot-bin"
                slate
                ghost
                xs
                class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
                :is-loading="loading[bot.id]"
                @click="openDeletePopup(bot)"
              />
            </div>
          </div>
        </li>
      </ul>
    </template>

    <AiAgentModal ref="aiAgentModalRef" @created="handleCreated" />

    <Dialog
      ref="aiAgentDeleteDialogRef"
      type="alert"
      :title="t('AI_AGENT.DELETE.CONFIRM.TITLE')"
      :description="
        t('AI_AGENT.DELETE.CONFIRM.MESSAGE', { name: selectedBotName })
      "
      :is-loading="uiFlags.isDeleting"
      :confirm-button-label="t('AI_AGENT.DELETE.CONFIRM.YES')"
      :cancel-button-label="t('AI_AGENT.DELETE.CONFIRM.NO')"
      @confirm="confirmDeletion"
    />
  </SettingsLayout>
</template>
